import React, { forwardRef, useEffect, useState } from 'react'

import {
  HiChevronDown,
  HiChevronUp,
  HiMagnifyingGlass,
  HiXMark,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { Input } from '@/components/common/TextInputV2'

export interface OfficeViewerFindBarProps {
  activeMatchIndex: number
  className?: string
  isSearching?: boolean
  matchCount: number
  onClose: () => void
  onNextMatch: () => void
  onPreviousMatch: () => void
  onQueryChange: (query: string) => void
  query: string
}

// How long a search runs before the reader is told it is running. Every
// keystroke starts a new one, and most finish well inside this.
const SEARCHING_LABEL_DELAY_MS = 400

export const isOfficeViewerFindShortcut = (
  event: Pick<
    KeyboardEvent | React.KeyboardEvent,
    'altKey' | 'ctrlKey' | 'key' | 'metaKey'
  >,
) =>
  !event.altKey &&
  (event.metaKey || event.ctrlKey) &&
  event.key.toLowerCase() === 'f'

export const OfficeViewerFindBar = forwardRef<
  HTMLInputElement,
  OfficeViewerFindBarProps
>(
  (
    {
      activeMatchIndex,
      className,
      isSearching = false,
      matchCount,
      onClose,
      onNextMatch,
      onPreviousMatch,
      onQueryChange,
      query,
    },
    inputRef,
  ) => {
    const hasMatches = matchCount > 0
    const matchCountLabel =
      query && hasMatches
        ? `${activeMatchIndex + 1} of ${matchCount}`
        : '0 of 0'

    // The count from the last keystroke stands until this one resolves. A
    // search that starts and finishes between two characters would otherwise
    // blink the count out and back on every letter typed.
    const [settledMatchCountLabel, setSettledMatchCountLabel] =
      useState(matchCountLabel)
    if (!isSearching && settledMatchCountLabel !== matchCountLabel) {
      setSettledMatchCountLabel(matchCountLabel)
    }

    const [isSearchSlow, setIsSearchSlow] = useState(false)
    useEffect(() => {
      if (!isSearching) {
        setIsSearchSlow(false)
        return
      }
      const handle = window.setTimeout(
        () => setIsSearchSlow(true),
        SEARCHING_LABEL_DELAY_MS,
      )
      return () => window.clearTimeout(handle)
    }, [isSearching])

    const matchLabel = isSearchSlow ? 'Searching...' : settledMatchCountLabel

    useEffect(() => {
      if (typeof inputRef === 'function') {
        return
      }
      inputRef?.current?.focus({ preventScroll: true })
      inputRef?.current?.select()
    }, [inputRef])

    return (
      <div
        className={twMerge(
          'absolute right-3 top-3 z-50 flex h-10 max-w-[calc(100%-1.5rem)] items-center overflow-hidden rounded-lg border border-primary-200 bg-white shadow-lg transition-[border-color,box-shadow] focus-within:border-accent-400 focus-within:ring-2 focus-within:ring-accent-400/40 dark:border-primary-700 dark:bg-primary-900 dark:focus-within:border-accent-400/50 dark:focus-within:ring-accent-400/30',
          className,
        )}
        data-office-viewer-find-bar
        role="search"
      >
        <HiMagnifyingGlass
          className="ml-3 h-4 w-4 shrink-0 text-primary-500 dark:text-primary-400"
          aria-hidden
        />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
              onClose()
              return
            }
            if (event.key !== 'Enter') {
              return
            }

            event.preventDefault()
            event.stopPropagation()
            if (isSearching || !hasMatches) {
              return
            }
            if (event.shiftKey) {
              onPreviousMatch()
            } else {
              onNextMatch()
            }
          }}
          className="h-full min-w-0 w-44 rounded-none border-0 bg-transparent px-2 text-sm outline-none focus-visible:border-0 focus-visible:ring-0 dark:bg-transparent sm:w-56"
          placeholder="Find in document"
          aria-label="Find in document"
          autoComplete="off"
          spellCheck={false}
        />
        {/* Fixed width, not a minimum: the label changes on every keystroke and
            the bar is anchored by its right edge, so any give here drags the
            input out from under the cursor. */}
        <span
          className="w-24 shrink-0 truncate px-2 text-right text-xs tabular-nums text-primary-500 dark:text-primary-400"
          aria-live="polite"
        >
          {matchLabel}
        </span>
        <div className="mx-1 h-5 w-px shrink-0 bg-primary-200 dark:bg-primary-700" />
        <button
          type="button"
          onClick={onPreviousMatch}
          disabled={!hasMatches || isSearching}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary-600 transition-colors hover:bg-primary-100 disabled:cursor-default disabled:opacity-35 dark:text-primary-300 dark:hover:bg-primary-800"
          aria-label="Previous match"
          title="Previous match (Shift+Enter)"
        >
          <HiChevronUp className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onNextMatch}
          disabled={!hasMatches || isSearching}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary-600 transition-colors hover:bg-primary-100 disabled:cursor-default disabled:opacity-35 dark:text-primary-300 dark:hover:bg-primary-800"
          aria-label="Next match"
          title="Next match (Enter)"
        >
          <HiChevronDown className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary-600 transition-colors hover:bg-primary-100 dark:text-primary-300 dark:hover:bg-primary-800"
          aria-label="Close find"
          title="Close find (Escape)"
        >
          <HiXMark className="h-4 w-4" aria-hidden />
        </button>
      </div>
    )
  },
)

OfficeViewerFindBar.displayName = 'OfficeViewer.FindBar'
